import {
  afterNextRender,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
} from '@angular/core';

@Directive({
  selector: '[blindspotHighlight]',
})
export class HighlightDirective {
  readonly searchTerm = input.required<string>({ alias: 'blindspotHighlight' });

  private static sharedHighlight: Highlight | null = null;
  private currentRanges: Range[] = [];
  private rendered = false;
  private elementRef = inject(ElementRef);

  constructor() {
    if (!HighlightDirective.sharedHighlight && CSS.highlights) {
      HighlightDirective.sharedHighlight = new Highlight();
      CSS.highlights.set(
        'blindspot-search',
        HighlightDirective.sharedHighlight,
      );
    }

    afterNextRender(() => {
      this.rendered = true;
      this.applyHighlight();
    });

    effect(() => {
      this.searchTerm();
      if (this.rendered) {
        this.applyHighlight();
      }
    });

    inject(DestroyRef).onDestroy(() => this.clearRanges());
  }

  private applyHighlight(): void {
    this.clearRanges();
    const search = this.searchTerm();
    if (!search) return;

    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    const walker = document.createTreeWalker(
      this.elementRef.nativeElement,
      NodeFilter.SHOW_TEXT,
    );

    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const text = node.textContent ?? '';
      let match;
      while ((match = regex.exec(text)) !== null) {
        const range = new Range();
        range.setStart(node, match.index);
        range.setEnd(node, match.index + match[0].length);
        this.currentRanges.push(range);
        HighlightDirective.sharedHighlight?.add(range);
      }
    }
  }

  private clearRanges(): void {
    for (const range of this.currentRanges) {
      HighlightDirective.sharedHighlight?.delete(range);
    }
    this.currentRanges = [];
  }
}
